import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Instagram, Globe } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { FilmCard } from '@/features/seasons/components/FilmCard'
import { useCompanyMember } from '../hooks/useCompanyMember'
import { AnimatedGradientBackground } from '@/components/common/AnimatedGradientBackground'
import { FadeInImage } from '@/components/common/FadeInImage'
import { withErrorCode } from '@/lib/errors'

const roleLabels: Record<string, string> = {
  choreographer: 'Choreographer',
  dancer: 'Dancer',
  videographer: 'Videographer',
  photographer: 'Photographer',
  'guest-artist': 'Guest Artist',
  intern: 'Intern',
}

export function MemberPage() {
  const { slug } = useParams<{ slug: string }>()
  const { member, choreographerFilms, castFilms, crewFilms, prev, next, loading, error } = useCompanyMember(slug)

  if (loading) {
    return (
      <div className="container mx-auto max-w-5xl px-4 py-16">
        <Skeleton className="mb-8 h-4 w-28" />
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[2fr_3fr]">
          <Skeleton className="w-full rounded-xl" style={{ aspectRatio: '9/16' }} />
          <div className="space-y-4 pt-2">
            <Skeleton className="h-9 w-52" />
            <Skeleton className="h-4 w-28" />
            <div className="flex gap-2">
              <Skeleton className="h-6 w-20 rounded-full" />
              <Skeleton className="h-6 w-24 rounded-full" />
            </div>
            <Skeleton className="h-4 w-36" />
            <div className="space-y-2 pt-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !member) {
    return (
      <div className="container mx-auto max-w-5xl px-4 py-16 text-center">
        <p className="text-destructive">
          {error ?? withErrorCode('Member not found.', 'company/member-not-found')}
        </p>
        <Link to="/company" className="mt-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to company
        </Link>
      </div>
    )
  }

  return (
    <div className="container mx-auto max-w-5xl px-4 py-16">
      <AnimatedGradientBackground />
      <div className="relative rounded-2xl bg-brand-white px-8 py-10 shadow-sm dark:bg-brand-black">
      <Link
        to="/company"
        className="mb-8 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> The Company
      </Link>

      {prev && (
        <Link
          to={`/company/${prev.slug}`}
          aria-label={`Previous member: ${prev.name}`}
          title={prev.name}
          className="group fixed left-2 top-1/2 z-40 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-brand-white/80 text-muted-foreground shadow-md backdrop-blur hover:bg-brand-white hover:text-foreground transition-colors dark:bg-brand-black/80 dark:hover:bg-brand-black sm:left-4 lg:h-14 lg:w-14"
        >
          <ArrowLeft className="h-5 w-5 lg:h-6 lg:w-6" />
        </Link>
      )}
      {next && (
        <Link
          to={`/company/${next.slug}`}
          aria-label={`Next member: ${next.name}`}
          title={next.name}
          className="group fixed right-2 top-1/2 z-40 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-brand-white/80 text-muted-foreground shadow-md backdrop-blur hover:bg-brand-white hover:text-foreground transition-colors dark:bg-brand-black/80 dark:hover:bg-brand-black sm:right-4 lg:h-14 lg:w-14"
        >
          <ArrowRight className="h-5 w-5 lg:h-6 lg:w-6" />
        </Link>
      )}

      <div className="grid grid-cols-1 gap-10 lg:grid-cols-[2fr_3fr]">
        {/* Left column — photo */}
        <div className="lg:sticky lg:top-20 lg:self-start">
          {member.portraitPhotoUrl ? (
            member.photoCredit ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="w-full overflow-hidden rounded-xl" style={{ aspectRatio: '9/16' }}>
                    <FadeInImage
                      src={member.portraitPhotoUrl}
                      alt={member.name}
                      className="h-full w-full object-cover"
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>{member.photoCredit}</TooltipContent>
              </Tooltip>
            ) : (
              <div className="w-full overflow-hidden rounded-xl" style={{ aspectRatio: '9/16' }}>
                <img
                  src={member.portraitPhotoUrl}
                  alt={member.name}
                  className="h-full w-full object-cover"
                />
              </div>
            )
          ) : (
            <div className="flex items-center justify-center">
              {member.photoCredit ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Avatar className="h-40 w-40 ring-4 ring-border ring-offset-2 ring-offset-background">
                      <AvatarImage
                    src={member.squarePhotoUrl ?? member.photoUrl}
                    alt={member.name}
                    className="animate-in fade-in duration-700 ease-out"
                  />
                      <AvatarFallback className="text-4xl">
                        {member.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                      </AvatarFallback>
                    </Avatar>
                  </TooltipTrigger>
                  <TooltipContent>{member.photoCredit}</TooltipContent>
                </Tooltip>
              ) : (
                <Avatar className="h-40 w-40 ring-4 ring-border ring-offset-2 ring-offset-background">
                  <AvatarImage
                    src={member.squarePhotoUrl ?? member.photoUrl}
                    alt={member.name}
                    className="animate-in fade-in duration-700 ease-out"
                  />
                  <AvatarFallback className="text-4xl">
                    {member.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                  </AvatarFallback>
                </Avatar>
              )}
            </div>
          )}
        </div>

        {/* Right column — content */}
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{member.name}</h1>
            {!member.isActive && (
              <span className="text-sm text-muted-foreground">Alumni</span>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {member.roles.map((role) => (
              <Badge key={role} variant="secondary">
                {roleLabels[role] ?? role}
              </Badge>
            ))}
          </div>

          {member.seasonNumbers.length > 0 && (
            <p className="text-sm text-muted-foreground">
              Seasons: {member.seasonNumbers.sort().join(', ')}
            </p>
          )}

          <div className="flex gap-4">
            {member.socialLinks?.instagram && (
              <a
                href={`https://www.instagram.com/${member.socialLinks.instagram.replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <Instagram className="h-4 w-4" />
                {member.socialLinks.instagram}
              </a>
            )}
            {member.socialLinks?.website && (
              <a
                href={member.socialLinks.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <Globe className="h-4 w-4" />
                Website
              </a>
            )}
          </div>

          {member.bio && (
            <p className="leading-relaxed text-muted-foreground">{member.bio}</p>
          )}

          {(choreographerFilms.length > 0 || castFilms.length > 0 || crewFilms.length > 0) && (
            <>
              <Separator />
              <h2 className="text-xl font-semibold">Films</h2>

              {choreographerFilms.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Choreographer</h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {choreographerFilms.map((film) => (
                      <FilmCard key={film.id} film={film} />
                    ))}
                  </div>
                </div>
              )}

              {castFilms.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Cast</h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {castFilms.map((film) => (
                      <FilmCard key={film.id} film={film} />
                    ))}
                  </div>
                </div>
              )}

              {crewFilms.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Crew</h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {crewFilms.map((film) => (
                      <FilmCard key={film.id} film={film} />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      </div>
    </div>
  )
}
